import {
  useEffect,
  useRef,
  useState
} from 'react'
import {
  useDispatch,
  useSelector
} from 'react-redux'
import {
  authSelectors,
  emailVerify,
  getUser,
  resetPassword,
  resetPasswordCodeVerify,
  setUserSectionPage,
  setVerificationEmailStandBy
} from 'store'
import { motion } from 'framer-motion'
import {
  Box,
  Button,
  HStack,
  Input,
  Spinner,
  Text
} from '@chakra-ui/react'
import { ImagesPng } from 'assets/images'
import {
  PENDING,
  SETTINGS,
  SUCCEEDED,
  VERIFICATION_TYPES
} from 'utils/constants'
import { COLORS } from 'utils/styles'

import { ResetPassword as ResetPasswordPage } from '../ResetPassword'

const MotionBox = motion(Box)

export const VerificationCodeModal = () => {
  const dispatch = useDispatch()
  const {
    emailVerifyStatus,
    resetPasswordCodeVerifyStatus,
    resetPasswordStatus,
    verificationEmailStandBy,
    verificationFor
  } = useSelector(authSelectors)

  const [dynamicIsOpen, setDynamicIsOpen] = useState(false)
  const [isCompleteCode, setIsCompleteCode] = useState(false)
  const [animateOut, setAnimateOut] = useState(false)
  const [isResetPasswordOpen, setIsResetPasswordOpen] = useState(false)
  const [headerText, setHeaderText] = useState(false)
  const [subHeaderText, setSubHeaderText] = useState(false)

  const inputs = Array.from({ length: 6 }, () => useRef(null))

  const headerBgPos = useRef({
    x: Math.floor(Math.random() * 601) - 600,
    y: Math.floor(Math.random() * 521) - 520
  })

  const inputsBgPos = useRef(
    Array.from({ length: 6 }, () => ({
      x: Math.floor(Math.random() * 151) - 150,
      y: Math.floor(Math.random() * 151) - 150
    }))
  )

  const checkInputValues = () => {
    let hasValue = 0

    inputs.map((ref) => {
      if (ref.current?.value) {
        hasValue += 1
      }

      return true
    })

    return hasValue === 6
  }

  const handleVerify = (data = {}) => {
    const code = inputs.map((ref) => ref.current?.value || '').join('')

    if (verificationFor === VERIFICATION_TYPES.ResetPassword) {
      const body = {
        token: code,
        email: verificationEmailStandBy,
        password: data.password,
        repeat_password: data.confirmPassword
      }
      dispatch(resetPassword(body))
    } else if (verificationFor === VERIFICATION_TYPES.ForgotPassword) {
      const body = {
        token: code,
        email: verificationEmailStandBy
      }
      dispatch(resetPasswordCodeVerify(body))
    } else {
      const body = {
        email: verificationEmailStandBy,
        token: code,
        purpose: verificationFor
      }
      dispatch(emailVerify(body))
    }
  }

  const handleChange = (e, idx) => {
    const { value } = e.target
    if (!/^\d?$/.test(value)) {
      return
    }

    if (value && idx < inputs.length - 1) {
      inputs[idx + 1].current?.focus()
    }

    setIsCompleteCode(checkInputValues())
  }

  const handleKeyDown = (e, idx) => {
    const allowedKeys = [
      'Backspace',
      'Tab',
      'ArrowLeft',
      'ArrowRight',
      'Enter',
      '0',
      '1',
      '2',
      '3',
      '4',
      '5',
      '6',
      '7',
      '8',
      '9'
    ]

    if (!/^\d?$/.test(e.target.value) || !allowedKeys.includes(e.key)) {
      e.preventDefault()
      return
    }

    if (e.key === 'Enter' && checkInputValues()) {
      handleVerify()
    }

    if (e.key === 'Backspace' && !e.target.value && idx > 0) {
      inputs[idx - 1].current?.focus()
    }
  }

  const handleBack = () => {
    const tempFor = verificationFor

    dispatch(setVerificationEmailStandBy({
      email: '',
      for: ''
    }))
    setTimeout(() => document.querySelector('.profile-icon').click(), 1050)

    if (tempFor === VERIFICATION_TYPES.ResetPassword) {
      setTimeout(() => setIsResetPasswordOpen(false), 1050)
    } else if (tempFor === VERIFICATION_TYPES.NewEmail) {
      setTimeout(() => dispatch(setUserSectionPage(SETTINGS)), 1600)
    }
  }

  const isLoading = emailVerifyStatus === PENDING || resetPasswordCodeVerifyStatus === PENDING

  useEffect(() => {
    if (verificationEmailStandBy) {
      setIsCompleteCode(false)
      setTimeout(() => setDynamicIsOpen(true), 1050)
    } else {
      setAnimateOut(true)
      setTimeout(() => {
        setAnimateOut(false)
        setDynamicIsOpen(false)
      }, 1050)
    }
  }, [verificationEmailStandBy])

  useEffect(() => {
    if (emailVerifyStatus === SUCCEEDED || resetPasswordStatus === SUCCEEDED) {
      const tempFor = verificationFor

      dispatch(setVerificationEmailStandBy({
        email: '',
        for: ''
      }))
      setTimeout(() => document.querySelector('.profile-icon').click(), 1050)

      if (tempFor === VERIFICATION_TYPES.NewEmail) {
        setTimeout(() => dispatch(setUserSectionPage(SETTINGS)), 1600)
        dispatch(getUser())
      } else if (tempFor === VERIFICATION_TYPES.ResetPassword) {
        setTimeout(() => setIsResetPasswordOpen(false), 1050)
      }
    }
  }, [emailVerifyStatus, resetPasswordStatus])

  useEffect(() => {
    if (resetPasswordCodeVerifyStatus === SUCCEEDED) {
      setIsResetPasswordOpen(true)
    }
  }, [resetPasswordCodeVerifyStatus])

  useEffect(() => {
    if (verificationFor === VERIFICATION_TYPES.Email) {
      setHeaderText('Confirm Your Email')
      setSubHeaderText(
        <Text fontSize="16px">
          We've sent a 6-digit verification code to
          <Text as="span" fontWeight="bold">{ ' ' + verificationEmailStandBy + ' ' }</Text>
          to complete your signup.
        </Text>
      )
    } else if (verificationFor === VERIFICATION_TYPES.NewEmail) {
      setHeaderText('Verify Your New Email')
      setSubHeaderText(
        <Text fontSize="16px">
          We've sent a 6-digit verification code to
          <Text as="span" fontWeight="bold">{ ' ' + verificationEmailStandBy + ' ' }</Text>
          to confirm your new email address.
        </Text>
      )
    } else if (verificationFor === VERIFICATION_TYPES.ForgotPassword) {
      setHeaderText('Forgot Password')
      setSubHeaderText(
        <Text fontSize="16px">
          We've sent a 6-digit verification code to
          <Text as="span" fontWeight="bold">{ ' ' + verificationEmailStandBy + ' ' }</Text>
          to reset your password.
        </Text>
      )
    } else {
      setHeaderText('Verification Code Confirmation')
      setSubHeaderText(
        <Text fontSize="16px">
          We've sent a 6-digit verification code to
          <Text as="span" fontWeight="bold">{ ' ' + verificationEmailStandBy}</Text>
        </Text>
      )
    }
  }, [verificationFor])

  return (
    <Box
      position="absolute"
      zIndex={dynamicIsOpen ? 1100 : -10}
      width="100svw"
      height="100svh"
      display="flex"
      alignItems="center"
      justifyContent="center"
      marginTop="-50px"
      backgroundColor="rgba(0, 0, 0, 0.6)"
      overflow="hidden"
    >
      {
        dynamicIsOpen
          ? (
            <>
              {
                isResetPasswordOpen
                  ? (
                    <ResetPasswordPage
                      animateOut={animateOut}
                      handleBack={handleBack}
                      handleVerify={handleVerify}
                      headerBgPos={headerBgPos}
                      ImagesPng={ImagesPng}
                      MotionBox={MotionBox}
                      resetPasswordStatus={resetPasswordStatus}
                    />
                  ) : ''
              }

              <MotionBox
                initial={{
                  y: animateOut ? 0 : window.innerHeight
                }}
                animate={{
                  y: animateOut ? window.innerHeight : 0
                }}
                transition={{
                  type: animateOut ? 'tween' : 'spring',
                  duration: 1
                }}
                width="90%"
                minWidth="40svw"
                maxWidth="650px"
                height="auto"
                padding="35px 30px"
                textAlign="center"
                overflow="hidden"
                backgroundColor="rgb(255, 255, 255)"
                borderRadius="10px"
                alignItems="center"
                position="absolute"
              >
                <Box
                  backgroundColor={COLORS.gold}
                  backgroundImage={`url(${ImagesPng.YellowMapTexture})`}
                  backgroundPosition={`${headerBgPos.current.x}px ${headerBgPos.current.y}px`}
                  padding="20px 30px"
                  border={`solid ${COLORS.darkGray} 1.5px`}
                  borderRadius="10px"
                  boxShadow="0 4px 10px rgba(0, 0, 0, 0.2)"
                  marginBottom="30px"
                >
                  <Text fontSize="25px" fontWeight="bold">{ headerText }</Text>
                  { subHeaderText }
                </Box>

                <HStack justify="center" justifyContent="space-between">
                  {
                    inputs.map((ref, idx) => {
                      const { x, y } = inputsBgPos.current[idx]

                      return (
                        <Input
                          ref={ref}
                          type="text"
                          maxLength={1}
                          inputMode="numeric"
                          textAlign="center"
                          fontSize="50px"
                          width="15%"
                          minWidth="25px"
                          height="10svh"
                          maxHeight="70px"
                          minHeight="25px"
                          placeholder="-"
                          backgroundColor={COLORS.gold}
                          backgroundImage={`url(${ImagesPng.YellowMapTexture})`}
                          backgroundSize="600px 300px"
                          backgroundRepeat="no-repeat"
                          backgroundPosition={`${x}px ${y}px`}
                          border={`solid ${COLORS.darkGray} 1.5px`}
                          borderRadius="10px"
                          boxShadow="0 4px 10px rgba(0, 0, 0, 0.2)"
                          padding="0"
                          onChange={(e) => handleChange(e, idx)}
                          onKeyDown={(e) => handleKeyDown(e, idx)}
                          sx={{
                            '@media (max-width: 600px)': {
                              fontSize: '40px'
                            }
                          }}
                        />
                      )
                    })
                  }
                </HStack>

                <HStack justify="center" justifyContent="space-evenly" marginTop="50px">
                  <Button
                    width="20%"
                    minWidth="100px"
                    backgroundColor={
                      isLoading
                        ? COLORS.gray
                        : COLORS.gold
                    }
                    pointerEvents={isLoading ? 'none' : 'auto'}
                    cursor={isLoading ? 'default' : 'cursor'}
                    _hover={{
                      backgroundColor: isLoading
                        ? COLORS.gray
                        : COLORS.gold_hover
                    }}
                    onClick={() => handleBack()}
                  >
                    { isLoading ? <Spinner size="md" /> : 'Back' }
                  </Button>

                  <Button
                    width="20%"
                    minWidth="100px"
                    backgroundColor={
                      isCompleteCode && !isLoading
                        ? COLORS.gold
                        : COLORS.gray
                    }
                    pointerEvents={isCompleteCode && !isLoading ? 'auto' : 'none'}
                    cursor={isCompleteCode && !isLoading ? 'cursor' : 'default'}
                    _hover={{
                      backgroundColor: isCompleteCode && !isLoading
                        ? COLORS.gold_hover
                        : COLORS.gray
                    }}
                    onClick={() => handleVerify()}
                  >
                    { isLoading ? <Spinner size="md" /> : 'Verify' }
                  </Button>
                </HStack>
              </MotionBox>
            </>
          ) : ''
      }
    </Box>
  )
}
